import React, { FC, useState } from 'react';
import { Animation } from '@dcl/ui/dist/modules/Modal/ModalConstants';
import {
  Button,
  FlexGrid,
  Form,
  Heading,
  Modal,
  Rank,
  Sizes,
  Typography,
  getClassNameFactory,
} from '@dcl/ui';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import {
  ButtonColor,
  ButtonKind,
  IconPosition,
} from '@dcl/ui/dist/elements/Button';
import CrossIcon from '../../../../icons/CrossIcon';
import { boldTypes } from '@dcl/ui/dist/elements/Heading';
import ClearFilterIcon from '../../../../icons/ClearFilterIcon';
import { IFilterModalSupportHours, IResultData } from './types';
import {
  FILTER_BUTTON_TEXT,
  FILTER_MODAL_APPLY_BUTTON_TEXT,
  FILTER_MODAL_CANCEL_BUTTON_TEXT,
  FILTER_MODAL_CLEAR_ARIA_LABEL,
  FILTER_MODAL_CLEAR_BUTTON_TEXT,
  FILTER_MODAL_CLOSE_ARIA_LABEL,
  FILTER_MODAL_Filter_BY,
  FILTER_MODAL_TITLE,
} from '../../../../constants';
import { cloneDeep } from 'lodash';
import './FilterModalSupportHours.css';
import ErrorIcon from '../../../../icons/ErrorIcon';

export const FilterModalSupportHours: FC<IFilterModalSupportHours> = (
  props
) => {
  const getClassName = getClassNameFactory(FilterModalSupportHours.displayName);
  const { handleCloseModal, resultData, handleAppliedFilter } = props;

  const clonedResultData = cloneDeep(resultData);
  const clonedResultData2 = cloneDeep(resultData);

  const [filterState, setFilterState] = useState<IResultData[]>(
    clonedResultData
  );
  const [appliedFilterState, setAppliedFilterState] = useState<IResultData[]>(
    clonedResultData2
  );
  const [hasError, setHasError] = useState(false);

  const applyFilters = () => {
    const appliedFilters: IResultData[] = filterState.map((filter) => {
      return {
        header: filter.header,
        accessor: filter.accessor,
        cols: filter.cols.map((option) => {
          return {
            option: option.option,
            selected: option.selected,
          };
        }),
      };
    });

    const clonedFilters = cloneDeep(appliedFilters);

    const isFilterSelected = clonedFilters.some((filter) =>
      filter.cols.some((col) => col.selected)
    );

    if (isFilterSelected) {
      setHasError(false);
      handleCloseModal();
    } else {
      setHasError(true);
      document.getElementById('error-body')?.scrollIntoView(true);
    }

    setAppliedFilterState(clonedFilters);
    handleAppliedFilter(clonedFilters);
  };

  const cancelFilters = () => {
    const previousFilterState: IResultData[] = appliedFilterState.map(
      (filter) => {
        return {
          header: filter.header,
          accessor: filter.accessor,
          cols: filter.cols.map((option) => {
            return {
              option: option.option,
              selected: option.selected,
            };
          }),
        };
      }
    );

    const clonedPreviousState = cloneDeep(previousFilterState);

    setFilterState(clonedPreviousState);

    handleCloseModal();
  };

  const resetFilters = () => {
    const updatedFilters = filterState.map((filter) => {
      const updatedCols = filter.cols.map((col) => {
        col.selected = false;
        return col;
      });
      filter.cols = updatedCols;
      return filter;
    });

    const clonedUpdatedFilters = cloneDeep(updatedFilters);

    setFilterState(clonedUpdatedFilters);
    handleAppliedFilter(clonedUpdatedFilters);
  };

  const toggleSelectAll = (
    e: React.ChangeEvent<HTMLInputElement>,
    header: string
  ) => {
    const updatedFilterState = filterState.map((filter) => {
      if (filter.header === header) {
        const updatedCols = filter.cols.map((col) => {
          col.selected = e.target.checked;
          return col;
        });
        filter.cols = updatedCols;
      }
      return filter;
    });

    const clonedUpdatedState = cloneDeep(updatedFilterState);
    const isFilterSelected = clonedUpdatedState.some((filter) =>
      filter.cols.some((col) => col.selected)
    );

    isFilterSelected && setHasError(false);

    setFilterState(clonedUpdatedState);
  };

  const toggleSingleCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>,
    header: string
  ) => {
    const updatedFilterState = filterState.map((filter) => {
      if (filter.header === header) {
        const updatedCols = filter.cols.map((col) => {
          if (col.option === event.target.value) {
            col.selected = event.target.checked;
          }
          return col;
        });
        filter.cols = updatedCols;
      }
      return filter;
    });

    const clonedUpdatedState = cloneDeep(updatedFilterState);
    const isFilterSelected = clonedUpdatedState.some((filter) =>
      filter.cols.some((col) => col.selected)
    );

    isFilterSelected && setHasError(false);

    setFilterState(clonedUpdatedState);
  };

  return (
    <Modal
      animation={Animation.SLIDEUP}
      className={getClassName({ descendantName: 'filterModal' })}
      ariaLabel={`Apply Filters Modal`}
      closeThisComponent={handleCloseModal}
      isCloseButtonHidden={true}
    >
      <FlexGrid className={getClassName({ descendantName: 'modalContainer' })}>
        <FlexGrid.Row>
          <FlexGrid.Col
            xs={9}
            sm={9}
            md={9}
            className={getClassName({ descendantName: 'modalHeader' })}
          >
            <FlexGrid.Row>
              <FlexGrid.Col xs={12} sm={12} md={12}>
                <Heading
                  rank={Rank.Third}
                  type={Sizes.MEDIUM}
                  boldType={boldTypes.medium}
                  className={getClassName({
                    descendantName: 'modalHeaderTitle',
                  })}
                >
                  {FILTER_BUTTON_TEXT}
                </Heading>
              </FlexGrid.Col>
              <FlexGrid.Col xs={12} sm={12} md={12}>
                <Typography
                  className={getClassName({
                    descendantName: 'modalHeaderDescription',
                  })}
                  type={Sizes.XXTRASMALL}
                  tag={TypographyTags.SPAN}
                >
                  {FILTER_MODAL_TITLE}
                </Typography>
              </FlexGrid.Col>
            </FlexGrid.Row>
          </FlexGrid.Col>
          <FlexGrid.Col
            xs={3}
            sm={3}
            md={3}
            className={getClassName({ descendantName: 'modalCloseButon' })}
          >
            <Button
              color={ButtonColor.TERTIARY}
              kind={ButtonKind.BUTTON}
              onClick={handleCloseModal}
              aria-label={FILTER_MODAL_CLOSE_ARIA_LABEL}
              data-testid="closeButton"
            >
              <CrossIcon />
            </Button>
          </FlexGrid.Col>
        </FlexGrid.Row>

        <FlexGrid.Row>
          <FlexGrid.Col xs={12} sm={12} md={12}>
            {hasError && (
              <div
                className={getClassName({ descendantName: 'error-body' })}
                id="error-body"
              >
                <span
                  className={getClassName({ descendantName: 'error-icon' })}
                >
                  {ErrorIcon}
                </span>
                <p className={getClassName({ descendantName: 'error-msg' })}>
                  Select at least one filter
                </p>
              </div>
            )}
          </FlexGrid.Col>
        </FlexGrid.Row>

        <FlexGrid.Row
          className={getClassName({ descendantName: 'modalHeaderFilterRow' })}
        >
          <FlexGrid.Col
            xs={5}
            sm={5}
            md={5}
            className={getClassName({
              descendantName: 'modalHeaderFilterTitleCol',
            })}
          >
            <Heading
              rank={Rank.Third}
              type={Sizes.SMALL}
              boldType={boldTypes.medium}
              className={getClassName({
                descendantName: 'modalHeaderFilterTitle',
              })}
            >
              {FILTER_MODAL_Filter_BY}
            </Heading>
          </FlexGrid.Col>
          <FlexGrid.Col xs={7} sm={7} md={7}>
            <Button
              block
              kind={ButtonKind.BUTTON}
              color={ButtonColor.TERTIARY}
              iconPosition={IconPosition?.LEFT}
              icon={<ClearFilterIcon />}
              aria-label={FILTER_MODAL_CLEAR_ARIA_LABEL}
              onClick={resetFilters}
              data-testid="clearFiltersButton"
            >
              {FILTER_MODAL_CLEAR_BUTTON_TEXT}
            </Button>
          </FlexGrid.Col>
        </FlexGrid.Row>
        <hr className={getClassName({ descendantName: 'modalRow' })}></hr>

        {filterState.map((filter, index) => {
          const keyIndex = index + 1;
          return (
            <FlexGrid.Row
              className={getClassName({ descendantName: 'filterContainer' })}
              key={keyIndex}
            >
              <FlexGrid.Col xs={12} sm={12} md={12}>
                <fieldset>
                  <legend>
                    <Typography
                      className={getClassName({
                        descendantName: 'modalLegend',
                      })}
                      type={Sizes.SMALL}
                      tag={TypographyTags.SPAN}
                    >
                      {filter.header}
                    </Typography>
                  </legend>
                  <FlexGrid.Row>
                    <FlexGrid.Col xs={12} sm={12} md={12}>
                      <Form.CheckboxControl
                        id={`checkbox_SelectALL_${keyIndex}`}
                        withOptionalLabel={false}
                        value={'Select All'}
                        labelText={'Select All'}
                        checked={filter.cols.every(
                          (checkBox) => checkBox.selected === true
                        )}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          toggleSelectAll(e, filter.header)
                        }
                      />
                      {filter.cols.map((col, index) => {
                        const colIndex = index + 1;
                        return (
                          <Form.CheckboxControl
                            key={colIndex + keyIndex}
                            id={`checkbox_${colIndex}_${keyIndex}`}
                            withOptionalLabel={false}
                            value={col.option}
                            labelText={col.option}
                            checked={col.selected}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => toggleSingleCheckbox(e, filter.header)}
                          />
                        );
                      })}
                    </FlexGrid.Col>
                  </FlexGrid.Row>
                </fieldset>
              </FlexGrid.Col>
            </FlexGrid.Row>
          );
        })}
        <hr className={getClassName({ descendantName: 'modalRow2' })}></hr>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} sm={12} md={12}>
            <Button
              block
              kind={ButtonKind.BUTTON}
              color={ButtonColor.SECONDARY}
              aria-label={FILTER_MODAL_CANCEL_BUTTON_TEXT}
              onClick={cancelFilters}
              data-testid="cancelButton"
            >
              {FILTER_MODAL_CANCEL_BUTTON_TEXT}
            </Button>
          </FlexGrid.Col>

          <FlexGrid.Col xs={12} sm={12} md={12}>
            <Button
              block
              kind={ButtonKind.BUTTON}
              color={ButtonColor.PRIMARY}
              aria-label={FILTER_MODAL_APPLY_BUTTON_TEXT}
              onClick={applyFilters}
              data-testid="applyButton"
            >
              {FILTER_MODAL_APPLY_BUTTON_TEXT}
            </Button>
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>
    </Modal>
  );
};

FilterModalSupportHours.displayName = 'FilterModalSupportHours';
